import { FileInfo } from "@/components/common/FileInfo/FileInfo"
import { Loading } from "@/components/common/Loading/Loading"
import { Typography } from "@/components/common/Typography/Typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useState } from "react"

interface QuestionnaireProcessingViewProps {
  fileName: string
}

const PROGRESS_STORAGE_KEY_PREFIX = "questionnaire_progress_"

/**
 * Displays a full loading/processing view for a questionnaire being extracted.
 * Composes the center loading spinner and right sidebar file info.
 * Used by both UploadPage and QuestionsPage when a questionnaire is processing.
 */
export const QuestionnaireProcessingView: React.FC<
  QuestionnaireProcessingViewProps
> = ({ fileName }) => {
  return (
    <>
      <ProcessingContentCenter />
      <ProcessingContentSidebar fileName={fileName} />
    </>
  )
}

/**
 * Center content: Loading spinner with progress percentage and status text.
 * Persists progress in localStorage and resumes from saved value.
 */
export const ProcessingContentCenter: React.FC<{
  questionnaireId?: string
  initialProgressValue?: number
}> = ({ questionnaireId, initialProgressValue = 1 }) => {
  const { translate } = useTranslation()
  const [isTimerComplete, setIsTimerComplete] = useState(false)

  // Get the localStorage key for this questionnaire
  const storageKey = questionnaireId
    ? `${PROGRESS_STORAGE_KEY_PREFIX}${questionnaireId}`
    : null

  // Load saved progress from localStorage or use initial value
  const getSavedProgress = (): number => {
    if (!storageKey) return initialProgressValue

    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const progress = parseInt(saved, 10)
        // Return saved progress if valid, otherwise use initial value
        return !isNaN(progress) && progress >= 0 && progress < 100
          ? progress
          : initialProgressValue
      }
    } catch {
      // Silently fallback to initial value if localStorage fails
    }
    return initialProgressValue
  }

  const [progressValue, setProgressValue] = useState(() => getSavedProgress())

  useEffect(() => {
    if (progressValue >= 100) {
      return
    }

    const interval = setInterval(() => {
      setProgressValue((current) => {
        if (current >= 100) {
          return current
        }

        const next = Math.min(current + 1, 100)

        if (storageKey && next < 100) {
          try {
            localStorage.setItem(storageKey, next.toString())
          } catch {
            // Silently ignore localStorage errors
          }
        }

        return next
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [storageKey, progressValue])

  useEffect(() => {
    if (progressValue >= 100 && storageKey) {
      try {
        localStorage.removeItem(storageKey)
      } catch {
        // skip error for localStorage removal failure
      }
      setIsTimerComplete(true)
    }
  }, [progressValue, storageKey])

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <Loading
        progressValue={progressValue}
        autoIncrementIntervalMs={0} // We handle increment ourselves
        onEndReached={() => setIsTimerComplete(true)}
        text={translate(
          isTimerComplete
            ? "QUESTIONNAIRE_QUESTIONS/TIMER_EXPIRED"
            : "QUESTIONNAIRE_QUESTIONS/TIMER_EXTRACTING_QUESTIONS",
        )}
      />
    </div>
  )
}

/**
 * Right sidebar content: File info card with questionnaire file name.
 */
export const ProcessingContentSidebar: React.FC<{
  fileName: string
}> = ({ fileName }) => {
  const { translate } = useTranslation()

  return (
    <div className="space-y-6">
      <Typography as="h2" variant="textM">
        {translate("QUESTIONNAIRE/YOUR_QUESTIONNAIRE")}
      </Typography>
      <FileInfo fileTitle={fileName} isLoading={false} />
    </div>
  )
}
